import { useEffect, useState } from 'react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
  ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { Users, Activity, Zap, Cpu, HardDrive, MemoryStick, Server } from 'lucide-react';
import { toast } from 'sonner';
import {
  dashboardSummary, dashboardTimeSeries, dashboardByModel, dashboardByUser,
  getServerHealth,
  type DashboardSummary, type TimeSeriesPoint, type ByModelPoint, type ByUserPoint,
  type ServerHealth,
} from '../services/admin_api';
import { extractErrorMessage } from '@/services/api';

// Dashboard is the landing page after admin login. Shows usage
// aggregates: top KPI strip, daily tokens line chart, per-model bar
// chart, and a per-user leaderboard.
export function Dashboard() {
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [series, setSeries] = useState<TimeSeriesPoint[]>([]);
  const [byModel, setByModel] = useState<ByModelPoint[]>([]);
  const [byUser, setByUser] = useState<ByUserPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [serverHealth, setServerHealth] = useState<ServerHealth | null>(null);

  // Poll server health every 10s so CPU / mem / disk gauges stay fresh
  // without page reload. The endpoint is cheap (gopsutil snapshot) so a
  // 10s cadence stays well below any meaningful load.
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const h = await getServerHealth();
        if (!cancelled) setServerHealth(h);
      } catch {
        // Non-fatal — admin still sees usage stats. Card just hides.
      }
    }
    load();
    const id = setInterval(load, 10_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const [s, ts, bm, bu] = await Promise.all([
          dashboardSummary(days),
          dashboardTimeSeries(days),
          dashboardByModel(days),
          dashboardByUser(days, 10),
        ]);
        if (cancelled) return;
        setSummary(s);
        setSeries(ts);
        setByModel(bm);
        setByUser(bu);
      } catch (err) {
        if (!cancelled) toast.error(extractErrorMessage(err, 'Failed to load dashboard'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [days]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Dashboard</h1>
          <p className="text-sm text-text-secondary">Usage overview across all users</p>
        </div>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="input w-auto"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      {/* Server health — OS-level snapshot, refreshed every 10s. */}
      {serverHealth && <ServerHealthPanel health={serverHealth} />}

      {/* Cost stat removed — product does not surface USD pricing. */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard icon={<Users className="h-4 w-4" />} label="Active users"
          value={summary ? `${summary.active_users} / ${summary.total_users}` : '—'} />
        <StatCard icon={<Activity className="h-4 w-4" />} label="Requests"
          value={summary ? summary.total_requests.toLocaleString() : '—'} />
        <StatCard icon={<Zap className="h-4 w-4" />} label="Tokens"
          value={summary ? summary.total_tokens.toLocaleString() : '—'} />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="card p-4">
          <h2 className="mb-3 text-sm font-semibold">Daily token usage</h2>
          <div className="h-64">
            {series.length === 0 ? (
              <EmptyChart loading={loading} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={series}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="date" fontSize={11} />
                  <YAxis fontSize={11} />
                  <Tooltip />
                  <Line type="monotone" dataKey="tokens" stroke="#2563EB" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="card p-4">
          <h2 className="mb-3 text-sm font-semibold">Tokens by model</h2>
          <div className="h-64">
            {byModel.length === 0 ? (
              <EmptyChart loading={loading} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={byModel} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis type="number" fontSize={11} />
                  <YAxis type="category" dataKey="model" width={140} fontSize={11} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="tokens" name="Tokens" fill="#2563EB" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      <div className="card p-4">
        <h2 className="mb-3 text-sm font-semibold">Top users by tokens</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase text-text-secondary">
                <th className="py-2 pr-4 font-medium">User</th>
                <th className="py-2 pr-4 font-medium">Requests</th>
                <th className="py-2 pr-4 font-medium">Tokens</th>
              </tr>
            </thead>
            <tbody>
              {byUser.length === 0 ? (
                <tr><td colSpan={3} className="py-6 text-center text-xs text-text-secondary">
                  {loading ? 'Loading…' : 'No usage in this period.'}
                </td></tr>
              ) : (
                byUser.map((u) => (
                  <tr key={u.user_id} className="border-b border-border/50">
                    <td className="py-2 pr-4">{u.email}</td>
                    <td className="py-2 pr-4">{u.requests.toLocaleString()}</td>
                    <td className="py-2 pr-4">{u.tokens.toLocaleString()}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ServerHealthPanel({ health }: { health: ServerHealth }) {
  return (
    <div className="card p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Server className="h-4 w-4 text-primary" />
          Server health
        </div>
        <div className="text-[11px] text-text-secondary">
          {health.hostname} · {health.platform || health.os} · up {formatUptime(health.uptime_seconds)}
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <ResourceGauge
          icon={<Cpu className="h-4 w-4" />}
          label="CPU"
          pct={health.cpu_usage_pct}
          primary={`${health.cpu_usage_pct.toFixed(1)}%`}
          secondary={`${health.cpu_cores} cores · load ${health.cpu_load_1.toFixed(2)} / ${health.cpu_load_5.toFixed(2)} / ${health.cpu_load_15.toFixed(2)}`}
        />
        <ResourceGauge
          icon={<MemoryStick className="h-4 w-4" />}
          label="Memory"
          pct={health.mem_usage_pct}
          primary={`${formatBytes(health.mem_used_bytes)} / ${formatBytes(health.mem_total_bytes)}`}
          secondary={`${formatBytes(health.mem_free_bytes)} free · ${health.mem_usage_pct.toFixed(1)}%`}
        />
        <ResourceGauge
          icon={<HardDrive className="h-4 w-4" />}
          label="Disk"
          pct={health.disk_usage_pct}
          primary={`${formatBytes(health.disk_used_bytes)} / ${formatBytes(health.disk_total_bytes)}`}
          secondary={`${formatBytes(health.disk_free_bytes)} free · ${health.disk_mountpoint || '/'}`}
        />
      </div>
    </div>
  );
}

function ResourceGauge({
  icon, label, pct, primary, secondary,
}: {
  icon: React.ReactNode;
  label: string;
  pct: number;
  primary: string;
  secondary: string;
}) {
  // Colour band: green < 60% < amber < 85% < red.
  const tone =
    pct >= 85 ? 'bg-red-500'
    : pct >= 60 ? 'bg-amber-500'
    : 'bg-emerald-500';
  const tonePale =
    pct >= 85 ? 'text-red-600'
    : pct >= 60 ? 'text-amber-600'
    : 'text-emerald-600';
  return (
    <div className="rounded-md border border-border/60 p-3">
      <div className="mb-1 flex items-center justify-between text-[11px] uppercase tracking-wider text-text-secondary">
        <div className="flex items-center gap-1.5">{icon}{label}</div>
        <div className={`tabular-nums ${tonePale}`}>{pct.toFixed(0)}%</div>
      </div>
      <div className="mb-1 h-2 w-full overflow-hidden rounded-full bg-black/5">
        <div
          className={`h-full rounded-full transition-all duration-300 ${tone}`}
          style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
        />
      </div>
      <div className="text-sm font-semibold tabular-nums">{primary}</div>
      <div className="text-[11px] text-text-secondary">{secondary}</div>
    </div>
  );
}

function formatBytes(b: number): string {
  if (b < 0 || !isFinite(b)) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let v = b;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function formatUptime(seconds: number): string {
  if (!seconds || seconds < 0) return '—';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function StatCard({
  icon, label, value,
}: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="card p-4">
      <div className="mb-1 flex items-center gap-2 text-xs text-text-secondary">
        {icon}
        {label}
      </div>
      <div className="text-xl font-semibold">{value}</div>
    </div>
  );
}

function EmptyChart({ loading }: { loading: boolean }) {
  return (
    <div className="flex h-full items-center justify-center text-xs text-text-secondary">
      {loading ? 'Loading…' : 'No data for this range.'}
    </div>
  );
}
